/**
 * Google 登录服务
 * 使用 Google Identity Services (GIS) 实现弹窗式登录
 * 文档：
 * 1. https://developers.google.com/identity/gsi/web/guides/overview
 * 2. https://developers.google.com/identity/gsi/web/reference/js-reference?hl=zh-cn
 * 3. https://developers.google.com/identity/protocols/oauth2/javascript-implicit-flow?hl=zh-cn
 * 第三方封装的SDK：https://devbaji.github.io/vue3-google-login/
 *
 * 安全特性：
 * - PKCE (Proof Key for Code Exchange) - 防止授权码拦截攻击
 * - STATE 验证 - 防止 CSRF 攻击
 */

import { GOOGLE_CONFIG } from '@/config/google'
import { GOOGLE_AUTH_TYPES, OAUTH_PROVIDERS } from '@/common/constants'
import i18n from '@/locales'
import {
  generateVerifier,
  generateChallenge,
  saveVerifier,
  generateState,
  saveState,
  validateOAuthEnvironment,
  generateNonce,
} from '@/utils/oauth'

const name = OAUTH_PROVIDERS.GOOGLE
const { t } = i18n.global
class GoogleAuthService {
  constructor() {
    this.initialized = false
    this.initPromise = null
  }

  /**
   * 初始化 Google Identity Services
   * 在应用启动时调用一次，使用缓存避免重复初始化
   * @returns {Promise<void>}
   */
  async initialize() {
    // 如果已经初始化，直接返回
    if (this.initialized) {
      return
    }

    // 如果正在初始化，返回同一个 Promise
    if (this.initPromise) {
      return this.initPromise
    }

    this.initPromise = new Promise((resolve, reject) => {
      // 检查是否已加载 GSI 脚本
      if (window.google?.accounts) {
        this.initialized = true
        resolve()
        return
      }

      // 动态加载 GSI 脚本
      const script = document.createElement('script')
      script.src = 'https://accounts.google.com/gsi/client'
      script.async = true
      script.defer = true

      script.onload = () => {
        this.initialized = true
        resolve()
      }

      script.onerror = () => {
        this.initPromise = null // 重置 Promise，允许重试
        reject(new Error(t('login.loadSDKFailed', { name })))
      }

      document.head.appendChild(script)
    })

    return this.initPromise
  }

  /**
   * 使用 ID Token 模式登录（推荐方式）
   * 根据设备类型选择不同的登录方式
   * @returns {Promise<{credential: string}>} Google ID Token (JWT)
   */
  async login() {
    console.log('[Google Auth] login', GOOGLE_CONFIG)
    // 验证 OAuth 环境安全性
    validateOAuthEnvironment()
    try {
      // 确保 SDK 已初始化
      await this.initialize()
      return await this.loginWithAuthCode()
    } catch (authError) {
      console.warn('[Google OAuth] Auth Code login failed, falling back to Redirect mode:', authError.message)
      return await this.loginWithRedirect()
    }
  }

  /**
   * 使用 Redirect 方式登录（适用于 iPhone Safari）
   * 跳转到 Google OAuth 授权页面，授权后重定向回来
   * @returns {Promise<never>} 不会返回，直接跳转页面
   */
  async loginWithRedirect() {
    // 1. 生成 PKCE 参数
    const codeVerifier = generateVerifier()
    const codeChallenge = await generateChallenge(codeVerifier)

    // 2. 生成 STATE 参数（防止 CSRF）
    const state = generateState()

    console.log('[Google OAuth Redirect] PKCE and STATE parameters generated')

    // 3. 保存到 sessionStorage
    saveVerifier(codeVerifier, OAUTH_PROVIDERS.GOOGLE)
    saveState(state, OAUTH_PROVIDERS.GOOGLE)

    // 4. 构建授权 URL
    const authUrl = new URL('https://accounts.google.com/o/oauth2/v2/auth')
    authUrl.searchParams.set('client_id', GOOGLE_CONFIG.clientId)
    authUrl.searchParams.set('redirect_uri', GOOGLE_CONFIG.redirectURI)
    authUrl.searchParams.set('response_type', 'code')
    authUrl.searchParams.set('scope', 'openid email profile')
    authUrl.searchParams.set('state', state)
    authUrl.searchParams.set('code_challenge', codeChallenge)
    authUrl.searchParams.set('code_challenge_method', 'S256')
    authUrl.searchParams.set('access_type', 'offline')
    authUrl.searchParams.set('prompt', 'select_account')

    console.log('[Google OAuth Redirect] Redirecting to authorization page:', authUrl.toString())

    // 5. 跳转到 Google 授权页面
    window.location.href = authUrl.toString()

    // 永远不会执行到这里，因为页面已经跳转
    return new Promise(() => { })
  }

  /**
   * 使用 OAuth 2.0 Authorization Code 模式登录（带 PKCE）
   * 获取 authorization code，用于服务端验证
   * @returns {Promise<{code: string, code_verifier: string}>} Google Authorization Code 和 code_verifier
   */
  async loginWithAuthCode() {
    // 生成 STATE 参数（防止 CSRF）
    const state = generateState()

    return new Promise((resolve, reject) => {
      try {
        const client = window.google.accounts.oauth2.initCodeClient({
          client_id: GOOGLE_CONFIG.clientId,
          scope: 'openid email profile',
          redirect_uri: GOOGLE_CONFIG.redirectURI,
          ux_mode: 'popup', // 使用弹窗模式
          // ux_mode: 'redirect', // 使用Redirect模式
          state, // 添加 state 参数
          callback: (response) => {
            console.log('OAuth Code Login response:', response)
            /**
             * response like: {
             *  authuser: '0',
             *  code: '',
             *  prompt: 'consent',
             *  scope: '',
             *  state?: '',
             *  error: '',
             *  error_uri?: '', // 提供额外信息的人类可读 ASCII 文本，用于帮助客户端开发者了解发生的错误。
             *  error_description?: '', // 一个 URI，用于标识包含错误相关信息的人类可读网页，以便为客户端开发者提供有关错误的更多信息。
             * }
             */
            if (response.error) {
              console.error('OAuth Code error:', response.error)
              if (response.error === 'access_denied') {
                resolve({
                  error: t('login.authCancelled')
                })
                return
              }
              reject(new Error(t('login.authenticationFailed', { name })))
              return
            }

            // 4. 验证 STATE（防止 CSRF 攻击）
            if (response.state) {
              const isValidState = response.state === state
              if (!isValidState) {
                reject(new Error(t('login.stateValidationFailed')))
                return
              }
            }

            resolve({
              type: GOOGLE_AUTH_TYPES.AUTH_CODE,
              ...response,
            })
          },
          error_callback: (error) => {
            console.error('OAuth code error_callback:', error)
            const { type } = error
            if (type === 'popup_closed') {
              resolve({
                error: t('login.authCancelled')
              })
            }
            reject(error)
          },
        })
        // 请求 authorization code
        client.requestCode()
      } catch (error) {
        console.error('OAuth code request error:', error)
        reject(error)
      }
    })
  }

  /**
   * 使用 One Tap 模式登录
   * google.accounts.id.initialize 方法会创建一个"使用 Google 账号登录"客户端实例，同一网页中的所有模块都可以隐式使用该实例。
   *
   * 即使您在同一网页中使用多个模块（例如一键登录、个性化按钮、撤消等），也只需调用一次 google.accounts.id.initialize 方法。
   * 如果您多次调用 google.accounts.id.initialize 方法，系统只会记住并使用最后一次调用中的配置。
   * 实际上，每当您调用 google.accounts.id.initialize 方法时，系统都会重置配置，并且同一网页中的所有后续方法都会立即使用新配置。
   * 警告： 即使您在同一网页中同时使用一键登录和按钮，也应仅调用一次 google.accounts.id.initialize 方法。
   *
   * ⚠️：谷歌 One Tap 的初衷是静默通过 prompt 尝试登录，是被动行为
   * 但是在我们这个场景里，是需要用户主动点击按钮才登录，虽然说也能这么做，但是存在被阻止而不显示 prompt ui 的情况
   * 也可以使用 renderButton 的方式主动触发，但是这个按钮样式不能根据业务自定义，并且也不能通过代码来唤起，所以这个也走不通，暂时不考虑使用 One Tap
   * @returns {Promise<{credential: string, nonce: string}>} Google ID Token (JWT) 和 nonce
   */
  async loginWithOneTap() {
    // 1. 生成 nonce（防止重放攻击）
    const nonce = generateNonce()

    return new Promise((resolve, reject) => {
      // 初始化 Google Identity Services
      window.google.accounts.id.initialize({
        client_id: GOOGLE_CONFIG.clientId,
        nonce, // 传递 nonce 给 Google，会被包含在 ID Token 的 nonce 声明中
        use_fedcm_for_prompt: GOOGLE_CONFIG.use_fedcm_for_prompt,
        use_fedcm_for_button: GOOGLE_CONFIG.use_fedcm_for_button,
        cancel_on_tap_outside: GOOGLE_CONFIG.cancel_on_tap_outside,
        button_auto_select: GOOGLE_CONFIG.button_auto_select,
        auto_select: GOOGLE_CONFIG.auto_select,
        callback: (response) => {
          console.log('[Google One Tap] One Tap login response:', response)
          if (response.error) {
            reject(new Error(response.error))
            return
          }
          /**
           * response like: { credential: '', select_by: 'fedcm', state: '' }
           */

          // 2. 返回 ID Token (JWT 格式) 和 nonce
          // ⚠️ nonce 需要在后端验证 ID Token 时使用
          // 后端应该解码 JWT，验证其中的 nonce 声明与客户端发送的 nonce 一致
          resolve({
            type: GOOGLE_AUTH_TYPES.ID_TOKEN,
            nonce, // 将 nonce 传递给后端用于验证
            ...response,
          })
        },
      })

      // window.google.accounts.id.renderButton(document.getElementById('google-login-btn'), {
      //   theme: 'outline',
      //   type: 'standard',
      //   size: 'large',
      //   text: 'signin_with',
      //   shape: 'rectangular',
      //   logo_alignment: 'left',
      //   width: '400',
      // })

      // 触发登录提示
      window.google.accounts.id.prompt((notification) => {
        /**
         * One Tap login prompt: pt{g: 'skipped', l: 'unknown_reason'}
         * One Tap login prompt: pt{g: 'skipped', l: 'tap_outside'}
         */
        // 处理提示未显示的情况
        /**
         * FedCM 迁移文档：https://developers.google.com/identity/gsi/web/reference/js-reference?hl=zh-cn#use_fedcm_for_button
         * 3. 为了加强用户隐私保护，google.accounts.id.prompt 回调不再在 PromptMomentNotication 对象中返回任何展示时刻通知。
         * 移除任何依赖于展示时刻相关方法的代码。它们是 isDisplayMoment()、isDisplayed()、isNotDisplayed() 和 getNotDisplayedReason() 方法
         * 4. 虽然仍会从 PromptMomentNotication 对象中的 google.accounts.id.prompt 回调中调用跳过时刻 isSkippedMoment()，但不会提供详细原因。从代码中移除所有依赖于 getSkippedReason() 方法的代码。
         * 请注意，启用 FedCM 后，已关闭的时刻通知 isDismissedMoment() 和相关的详细原因方法 getDismissedReason() 保持不变。
         */
        if (notification.isSkippedMoment()) {
          const reason = notification.getSkippedReason()
          console.warn('Google login skipped:', reason)

          // 用户主动跳过或关闭
          if (reason === 'user_cancel' || reason === 'tap_outside') {
            reject(new Error(t('login.authCancelled')))
          } else {
            reject(new Error(t('login.authCancelled')))
          }
        }
      })
    })
  }

  /**
   * 注销 Google 登录
   * 清除自动登录状态，撤销授权
   * @param {string} [email] - 用户邮箱（可选）
   */
  async logout(email) {
    try {
      if (window.google?.accounts?.id) {
        // 禁用自动选择
        window.google.accounts.id.disableAutoSelect()

        // 如果提供了邮箱，撤销该账号的授权
        if (email) {
          window.google.accounts.id.revoke(email, (done) => {
            if (done.error) {
              console.error('Google account revoke error:', done.error)
            } else {
              console.log('Google account revoked successfully')
            }
          })
        }
      }
    } catch (error) {
      console.error('Google logout failed:', error)
      throw new Error(t('login.logoutFailed', { name }))
    }
  }

  /**
   * 检查是否已初始化
   * @returns {boolean}
   */
  isInitialized() {
    return this.initialized
  }
}

// 导出单例
export default new GoogleAuthService()
